/**
 * @lines98/shared/config/meta — typed config constants for the «Ювелир» meta layer.
 *
 * ЗАЧЕМ / WHY THIS EXISTS: CLAUDE.md hard-rule + code-structure §4 + AC-11
 * («config-not-hardcode») — ВСЕ числа меты (пороги добычи, per-tick шанс-кривые,
 * кэпы, карат) живут здесь, не литералами в логике faucet'а. Один источник →
 * клиент и сервер не расходятся (как `balance.ts`). Faucet-модуль (`server/src/meta/`)
 * читает эти значения; сам никаких чисел не несёт.
 *
 * SCOPE (серверная добыча / faucet):
 *   - Золото: `GOLD_PER_POINT_THRESHOLD[mode]`, `DAILY_GOLD_MULTIPLIER`.
 *   - Осколки: `SHARDS_MIN_CLEAR`, `SHARDS_MODE_BASE[mode]`, `SHARDS_SIZE_BONUS(n)`.
 *   - Самоцветы: `GEM_MIN_CLEAR` (базовый камень n≥10 → пул; гном-кран — meta-gnomes).
 *
 * **«Животные» (jewelry) удалены (workshop-animals-removal.md, owner п.14 2026-06-21):**
 * зверь-конфиг (Jewelry-типы/профиль/уровни/чары, цены крафта-апгрейда, дерево рецептов,
 * per-tick бонус-кривые рычагов + карат-как-добавка, потолки 2-го камня, сплав осколков
 * в камень, рост цены карата) СНЯТ — бонусов животных нет (AC-1/AC-2). Сохранён тип
 * `JewelryCost` (наградами прогрессии переиспользуется). Карат-ДАННОЕ — паркуется.
 *
 * SCOPE (радуга / камни-ресурс):
 *   - Радуга: `RAINBOW_COLOR_COUNT` (=7), `RAINBOW_FULL_MASK` (=127), `colorBit()`,
 *     `GEM_NAME_BY_COLOR` (1..7 → имена камней) — нужны пулу камней / ачивкам / аватару.
 *
 * SCOPE (камни как ресурс — `meta-gems-resource.md` T1):
 *   - Пул камней: `GEM_POOL_COLORS` (=`RAINBOW_COLOR_COUNT`). Цвет добытого камня —
 *     ВСЕГДА цвет клира (и базовый, и бонусный; owner-decision 2026-06-10 §2.2/§11):
 *     цвет НЕ выбирается RNG → отдельной config-функции выбора цвета НЕТ.
 *
 * SCOPE (Срез 4a — уровень игрока + ачивки; `meta-jeweler-progression.md` §3):
 *   - XP-источники: `XP_PER_GAME_BASE`, `XP_PER_POINT_THRESHOLD[mode]`,
 *     `XP_DAILY_BONUS`, `XP_SKILL_EVENT` (cap 1/день), `XP_MODE_COVERAGE_BONUS`
 *     (cap 3/день).
 *   - XP-кривая: `PLAYER_XP_BASE`, `PLAYER_XP_GROWTH`, `PLAYER_LEVEL_SOFT_CAP`,
 *     `PLAYER_XP_POST_CAP_STEP` + чистые функции `xpToNextLevel(L)` / `levelForXp(xp)`.
 *   - Веха-награды: `PLAYER_LEVELUP_GOLD`/`PLAYER_LEVELUP_SHARDS`,
 *     `PLAYER_MILESTONE_LEVELS`, `PLAYER_SLOT_REWARD_LEVELS`, `PLAYER_MILESTONE_BUNDLE`.
 *   - Ачивки (ПЕРЕСБОРКА под «Гномов», `docs/specs/achievements.md` §3 + balance v1):
 *     `ACHIEVEMENT_CATALOG` (28, id/категория/триггер/тиры) + пороги `ACH_*_TIER` +
 *     `ACH_REWARD[rarity]` (3 класса редкости: частая/редкая/очень-редкая).
 *
 * NOT IN SCOPE (Срез 4+):
 *   - `PLAYER_TITLE_BY_MILESTONE` / `ACH_TITLE_BY_ID` (owner/art, O-PL-9 —
 *     косметика, не блокер; titul-флекс маппинг проставляется позже).
 *   - P2w-чары-числа (`CHARM_PROC_*`) — forward-compat данные, эффект спит (O-10).
 *   Эти константы добавятся в этот же файл по срезам (forward-only расширение).
 *
 * SOURCES (числа — НЕ выдуманы; взяты из спеки/баланса):
 *   - meta-jeweler.md §11 (реестр конфиг-констант) + §2/§4 (поведение).
 *   - meta-jeweler-progression.md §3 (Срез 4a — XP-кривая / веха-награды /
 *     каталог ачивок; числа `[balance v4]`).
 *   - balance v4 — `docs/balance/meta-jeweler/jeweler-balance-report-v4.{md,html}`
 *     (XP/уровень/ачивки) + `v4_ach_thresholds.csv` (пороги ачивок).
 *   - balance v5/v6 — `docs/balance/meta-jeweler/jeweler-balance-report-v6-rounding.md`
 *     (округлённые «красивые» числа) + `-v5.md` (формы/кэпы).
 *   - ADR-0015 faucet-module.md (как faucet их читает) + data-model.md.
 *   - ADR-0014 (p2w-стена: эти числа НИКОГДА не входят в `lines98.ts`/`replayScore`).
 *
 * Cross-references:
 *   - `packages/shared/src/config/balance.ts` — образец config-дисциплины.
 *   - `packages/server/src/meta/` — единственный потребитель этих чисел.
 */
// =============================================================================
// Золото (meta-jeweler.md §2.1 + §11 «Золото»)
// =============================================================================
/**
 * Очков за 1 золото по режиму («тик золота»). **owner-fixed 2026-06-04 (E-3,
 * вариант A): 100 / 90 / 80** (Предсказание / Классика / Вслепую). Это ПОРОГ,
 * не множитель — тяжёлый режим платит щедрее за очко. Каждые
 * `GOLD_PER_POINT_THRESHOLD[mode]` очков = 1 базовая монета = 1 тик
 * (на тике кидается per-tick шанс бонуса). Доска дня = порог Предсказания
 * (predict-only фикс, §10 O-8). meta-jeweler.md §11 «Золото».
 */
export const GOLD_PER_POINT_THRESHOLD = {
    predict: 100,
    classic: 90,
    blind: 80,
};
/**
 * Множитель золота за зачётную партию доски дня. **= 5** (balance v3 — дневная
 * партия ≈ 5 Zen-партий золота). Применяется к базовому золоту ДО per-tick
 * бонусов (база растёт → больше тиков). meta-jeweler.md §11 «Золото».
 */
export const DAILY_GOLD_MULTIPLIER = 5;
// =============================================================================
// Осколки (meta-jeweler.md §2.2 + §11 «Осколки»)
// =============================================================================
/**
 * Минимальный `n` клира (число снятых клеток) для осколков. **= 9, owner-fixed
 * 2026-06-04** (не TBD; конфиг — на случай ребаланса). Бар единый во всех
 * режимах. meta-jeweler.md §11 «Осколки».
 */
export const SHARDS_MIN_CLEAR = 9;
/**
 * Плоская база осколков за квалифицирующий клир по режиму. **owner-set
 * 2026-06-04: Предсказание 1 / Классика 2 / Вслепую 3** (премия за сложность
 * = +1 на ступень режима — собрать девятку без подсказок тяжело). Композиция с
 * размером АДДИТИВНАЯ: `осколки = SHARDS_MODE_BASE[mode] + SHARDS_SIZE_BONUS(n)`.
 * meta-jeweler.md §2.2 / §11 «Осколки».
 */
export const SHARDS_MODE_BASE = {
    predict: 1,
    classic: 2,
    blind: 3,
};
/**
 * Аддитивный нелинейный бонус осколков за размер клира `n` (режимо-независимый).
 * **= `(n − SHARDS_MIN_CLEAR)²`** (balance v3): {n=9:0, 10:1, 11:4, 12:9, 13:16}.
 * Super-linear (длинная линия несоизмеримо сложнее → платит кратно), но мода-разрыв
 * остаётся плоским (прибавляется, не умножает базу). `= 0` при `n ≤ 9`.
 * meta-jeweler.md §11 «Осколки».
 *
 * @param n — число снятых клеток за событие клира.
 * @returns бонус осколков (целое ≥ 0).
 */
export function SHARDS_SIZE_BONUS(n) {
    if (n <= SHARDS_MIN_CLEAR)
        return 0;
    const d = n - SHARDS_MIN_CLEAR;
    return d * d;
}
// =============================================================================
// Самоцветы / радуга (meta-jeweler.md §2.3 + §11 «Самоцветы и радуга»)
// =============================================================================
/**
 * Минимальный `n` клира для выпадения самоцвета. **= 10** (owner-fixed
 * 2026-06-04): `n ≥ 10` роняет ровно 1 камень детерминированно (100%), цвет =
 * цвет клира. `n = 9` (вкл. крест 5+5) камня НЕ даёт. На 9×9 `n ≥ 10` физически
 * достижимо только через пересечение. meta-jeweler.md §2.3 / I-5.
 */
export const GEM_MIN_CLEAR = 10;
// =============================================================================
// Радуга / шедевр / карат (Срез 3a; meta-jeweler.md §2.4 / §4.2 / §4.3 + §11)
// =============================================================================
/**
 * Число цветов в радуге = число цветов шаров движка. **= 7** (зафиксировано
 * концепт-стадией: 7 цветов). Радуга = ровно 7 ячеек (§2.4). meta-jeweler.md §2.4.
 */
export const RAINBOW_COLOR_COUNT = 7;
/**
 * Битовая маска полной радуги (все 7 цветов закрыты). **= `0b1111111` = 127**.
 * `closed_colors === RAINBOW_FULL_MASK` ⇔ радуга 7/7 (условие шедевра, AC-6).
 * Выводится из `RAINBOW_COLOR_COUNT` — не magic-литерал. meta-jeweler.md §4.2.
 */
export const RAINBOW_FULL_MASK = (1 << RAINBOW_COLOR_COUNT) - 1;
/**
 * Бит цвета в bitset радуги. Цвет `colorIndex` 1..7 (порядок цветов движка) →
 * бит `colorIndex − 1` (0..6). `colorBit(1) = 0b1` (рубин), `colorBit(7) = 0b1000000`
 * (турмалин). Маппинг стабилен к индексам движка (data-model.md «closed_colors»).
 *
 * @param colorIndex — цвет 1..`RAINBOW_COLOR_COUNT` (как у движка / клира).
 * @returns битовая маска одного цвета.
 */
export function colorBit(colorIndex) {
    return 1 << (colorIndex - 1);
}
/**
 * Число цветов в пуле камней-ресурса = число цветов движка = `RAINBOW_COLOR_COUNT`
 * (`meta-gems-resource.md` §2.1). Пул = ровно 7 per-color целочисленных счётчиков на
 * игрока. Выведено из `RAINBOW_COLOR_COUNT` (не magic-литерал) — пул и радуга
 * разделяют одно и то же множество цветов 1..7.
 */
export const GEM_POOL_COLORS = RAINBOW_COLOR_COUNT;
// NOTE (owner-decision 2026-06-10, §2.2/§11): цвет добытого камня — ВСЕГДА цвет клира
// (и базовый, и бонусный 2-й). Бонусный камень больше НЕ выбирает случайный цвет из 7
// → бывшая `gemColorFromRoll(roll)` СНЯТА (мёртвый код). RNG faucet'а задействован
// только на ШАНС выпадения бонусного камня, не на цвет (faucet.ts golden-порядок D-2).
/**
 * Маппинг `colorIndex` 1..7 → имя камня (RESOLVED §10 O-5, game-designer
 * 2026-06-05 `[GD-default финал]`). Индекс 0 — sentinel (цветов с индексом 0 нет).
 * Стабилен к индексам движка (НЕ к палитре скина — скин может менять оттенки,
 * индексы/имена постоянны). Имена — финал (стабильные ключи/строки для тикетов);
 * визуальные оттенки — art. meta-jeweler.md §4.4 / §11 `GEM_NAME_BY_COLOR`.
 */
export const GEM_NAME_BY_COLOR = [
    '', // [0] sentinel
    'ruby', // 1 — рубин
    'sapphire', // 2 — сапфир
    'emerald', // 3 — изумруд
    'topaz', // 4 — топаз
    'amethyst', // 5 — аметист
    'citrine', // 6 — цитрин
    'tourmaline', // 7 — турмалин
];
/**
 * RU-имена камней по `colorIndex` 1..7 — ЕДИНЫЙ источник русских подписей для UI
 * (confirm-диалоги вставки/сплава, aria-метки HUD/свотчей). Раньше тройная локальная
 * копия (workshop-screen / beast-detail / gem-pool-hud / rainbow-tray), и копии РАЗОШЛИСЬ
 * с каноном: индексы 2↔3 были перепутаны (сапфир/изумруд) → скрытый баг. Этот массив —
 * перевод `GEM_NAME_BY_COLOR` 1:1 (тот же порядок индексов): 2 = сапфир, 3 = изумруд.
 * Меняешь канон-имена — меняешь здесь же (одна правка, не четыре). Индекс 0 — sentinel.
 */
export const GEM_RU_NAME_BY_COLOR = [
    '', // [0] sentinel
    'рубин', // 1 — ruby
    'сапфир', // 2 — sapphire
    'изумруд', // 3 — emerald
    'топаз', // 4 — topaz
    'аметист', // 5 — amethyst
    'цитрин', // 6 — citrine
    'турмалин', // 7 — tourmaline
];
// SHARD_TO_GEM_COST (сплав осколков → камень мастерской) + KARAT_COST_GROWTH
// (cost-gating сплава/представления шедевра) УДАЛЕНЫ вместе с «животными»
// (workshop-animals-removal.md): forge/present-masterpiece мутации сняты, 0 потребителей.
// Карат-ДАННОЕ на meta_balances сохранено (паркуется Q6/Q8); борд «Мастер-ювелир» жив.
// =============================================================================
// Уровень игрока (Срез 4a; meta-jeweler-progression.md §1 + §3, balance v4)
//
// Личный уровень = широкий odometer аккаунта (НЕ множитель добычи — то карат;
// НЕ сила артефакта — то уровень-изделия). XP капает только за верифицированную
// игру (I-3). Награды p2w-safe (золото/осколки/слот Книги/титул, I-1). Все числа
// из progression.md §3 (`[balance v4]`); НЕ выдуманы.
// =============================================================================
/**
 * XP за факт завершённой верифицированной партии (любой режим). **balance v4: = 0**
 * — за факт партии XP НЕ даётся: пустая партия = 0 XP (структурный анти-grind,
 * AC-PL2). Константа оставлена ручкой ребаланса (мала по design). progression.md
 * §1.1 / §3 `XP_PER_GAME_BASE`.
 */
export const XP_PER_GAME_BASE = 0;
/**
 * Очков за 1 XP по режиму (доминирующий источник — мастерство → опыт). **balance
 * v4: predict 3 / classic 2.4 / blind 2** (тяжёлый режим дешевле, как золото).
 * XP за очки = `floor(score / XP_PER_POINT_THRESHOLD[mode])`. progression.md §1.1 /
 * §3 `XP_PER_POINT_THRESHOLD`.
 */
export const XP_PER_POINT_THRESHOLD = {
    predict: 3,
    classic: 2.4,
    blind: 2,
};
/**
 * XP за зачёт доски дня (поддержка дневного ритуала, 1/день — идемпотентно через
 * `daily_attempts` PK). **balance v4: = 30**. progression.md §1.1 / §3 `XP_DAILY_BONUS`.
 */
export const XP_DAILY_BONUS = 30;
/**
 * XP за ПЕРВЫЙ за день клир `n≥9`/`n≥10` (скилл-событие, не grind). **balance v4:
 * = 6** (кап 1/день — серверная дневная агрегат-логика). progression.md §1.1 / §3
 * `XP_SKILL_EVENT`.
 */
export const XP_SKILL_EVENT = 6;
/**
 * XP за «новый режим сегодня» (покрытие — поощряет разнообразие). **balance v4:
 * = 12** (кап 3/день: максимум 3 режима = 3 начисления/сутки). progression.md §1.1
 * / §3 `XP_MODE_COVERAGE_BONUS`.
 */
export const XP_MODE_COVERAGE_BONUS = 12;
/** Минимальный `n` клира, считающийся скилл-событием для `XP_SKILL_EVENT` (= `SHARDS_MIN_CLEAR`=9). */
export const XP_SKILL_EVENT_MIN_CLEAR = SHARDS_MIN_CLEAR;
/** Дневной кап числа начислений `XP_MODE_COVERAGE_BONUS` (= число режимов = 3). */
export const XP_MODE_COVERAGE_DAILY_CAP = 3;
/** Дневной кап числа начислений `XP_SKILL_EVENT` (1 — первый скилл-клир за день). */
export const XP_SKILL_EVENT_DAILY_CAP = 1;
/**
 * XP для уровня 1→2 (база геометрической кривой, RuneScape-тип). **balance v4: = 60**.
 * progression.md §1.2 / §3 `PLAYER_XP_BASE`.
 */
export const PLAYER_XP_BASE = 60;
/**
 * Множитель геометрического роста XP-to-next по уровню (самая чувствительная ручка
 * кривой). **balance v4: = 1.10** (1.08/1.12 = L50 за 62/252 дня). progression.md
 * §1.2 / §3 `PLAYER_XP_GROWTH`.
 */
export const PLAYER_XP_GROWTH = 1.1;
/**
 * Уровень мягкого потолка: после `PLAYER_LEVEL_SOFT_CAP` `xp_to_next` переходит на
 * плоский шаг (числа XP читаемы на хвосте, как RuneScape virtual-levels 99→120).
 * **balance v4: = 60**. Hard-cap'а нет — уровень бесконечен (но без множителя-силы).
 * progression.md §1.2 / §3 `PLAYER_LEVEL_SOFT_CAP`.
 */
export const PLAYER_LEVEL_SOFT_CAP = 60;
/**
 * Плоский XP-шаг после soft-cap'а. **balance v4 (owner-approved): = 16608.**
 *
 * Это `round(PLAYER_XP_BASE · PLAYER_XP_GROWTH^(SOFT_CAP−1))` = `round(60·1.10^59)`
 * = последний ГЕОМЕТРИЧЕСКИЙ шаг (на L60) → гладкая стыковка: кривая НЕ прыгает на
 * потолке (`xpToNext(60)` = `xpToNext(61)` = 16608). _Прим.: progression.md §3
 * описывает это как «xp_to_next(61)»; буквально `xpToNextLevel(61)=round(60·1.10^60)=
 * 18269`, но owner-approved balance-число = 16608 (=шаг L60). Принят balance-число
 * как authoritative (числа из balance, не пересчёт спеки) — расхождение во flag PR._
 * progression.md §1.2 / §3 `PLAYER_XP_POST_CAP_STEP`. Проверяется тестом.
 */
export const PLAYER_XP_POST_CAP_STEP = 16608;
/**
 * XP, требуемый для перехода с уровня `L` на `L+1` (1-based). Геометрический рост
 * до soft-cap'а, далее плоский шаг (`PLAYER_XP_POST_CAP_STEP`). Чистая функция
 * config — единственный источник правды кривой (сервер денормализует level в
 * `meta_balances`, клиент рендерит прогресс из тех же чисел). AC-PL3.
 *
 * @param level — текущий уровень `L ≥ 1`.
 * @returns XP, нужный чтобы достичь `L+1` (целое ≥ 1).
 */
export function xpToNextLevel(level) {
    if (level < 1)
        return PLAYER_XP_BASE; // защитно — уровень всегда ≥ 1
    if (level > PLAYER_LEVEL_SOFT_CAP)
        return PLAYER_XP_POST_CAP_STEP;
    return Math.round(PLAYER_XP_BASE * Math.pow(PLAYER_XP_GROWTH, level - 1));
}
/**
 * Суммарный XP, необходимый, чтобы С НУЛЯ достичь уровня `level` (total_xp(L) =
 * Σ xp_to_next(i), i in 1..L-1). `totalXpForLevel(1) = 0`. Монотонно растёт.
 *
 * @param level — целевой уровень `L ≥ 1`.
 * @returns накопленный XP-порог входа в уровень `L`.
 */
export function totalXpForLevel(level) {
    let total = 0;
    for (let i = 1; i < level; i++)
        total += xpToNextLevel(i);
    return total;
}
/**
 * Текущий уровень игрока по накопленному XP (обратная к `totalXpForLevel`). Уровень
 * бесконечен (нет hard-cap'а) — после soft-cap'а шаг плоский, цикл сходится за
 * `xp / PLAYER_XP_POST_CAP_STEP` итераций даже на огромных XP. AC-PL3.
 *
 * @param xp — накопленный XP игрока (целое ≥ 0).
 * @returns уровень `L ≥ 1`.
 */
export function levelForXp(xp) {
    let level = 1;
    let consumed = 0;
    // Идём по уровням, вычитая стоимость каждого, пока хватает XP.
    for (;;) {
        const next = xpToNextLevel(level);
        if (consumed + next > xp)
            return level;
        consumed += next;
        level += 1;
    }
}
/**
 * Прогресс к следующему уровню для рендера (xp в уровне / xp нужно на уровень).
 * Чистая функция — клиент берёт уже посчитанную проекцию (server-truth, AC-PL5),
 * но та же функция доступна и фронту для прогресс-бара из одного источника.
 *
 * @param xp — накопленный XP игрока.
 * @returns `{ level, xpIntoLevel, xpForLevel, xpToNext }`.
 */
export function levelProgress(xp) {
    const level = levelForXp(xp);
    const base = totalXpForLevel(level);
    const xpForLevel = xpToNextLevel(level);
    const xpIntoLevel = xp - base;
    return { level, xpIntoLevel, xpForLevel, xpToNext: xpForLevel - xpIntoLevel };
}
/**
 * Мелкая награда за КАЖДЫЙ уровень (золото / осколки) — «smooth»-тикл между вехами
 * (owner 2026-06-05 «немного, чтобы баланс не сломать»). **balance v4: 1 / 1** (к
 * L60 = 1.6% золота за период). progression.md §1.3 / §3 `PLAYER_LEVELUP_GOLD`/`_SHARDS`.
 */
export const PLAYER_LEVELUP_GOLD = 1;
export const PLAYER_LEVELUP_SHARDS = 1;
/**
 * Множество веха-уровней, на которых выдаётся РАЗОВЫЙ пакет золота/осколков
 * (`PLAYER_MILESTONE_BUNDLE`). **balance v4: {15, 30, 60}** (пакеты позже, чтобы не
 * давить ранний faucet). progression.md §1.3 / §3 `PLAYER_MILESTONE_LEVELS`.
 */
export const PLAYER_MILESTONE_LEVELS = [15, 30, 60];
/**
 * Уровни, на которых выдаётся слот «Книги реплеев» (фикс-расписание; ЕДИНСТВЕННЫЙ
 * источник слотов — ачивки слотов НЕ дают, AC-PL5/AC-A3). **balance v4: {5, 15, 30,
 * 50}** (4 слота). progression.md §1.3 / §3 `PLAYER_SLOT_REWARD_LEVELS`.
 */
export const PLAYER_SLOT_REWARD_LEVELS = [5, 15, 30, 50];
/**
 * Крупный разовый пакет за веху уровня (`PLAYER_MILESTONE_LEVELS`). **balance v4:
 * 15 золота + 12 осколков**. progression.md §1.3 / §3 `PLAYER_MILESTONE_BUNDLE`.
 */
export const PLAYER_MILESTONE_BUNDLE = { gold: 15, shards: 12 };
/**
 * Награда по классу редкости (ТОЛЬКО золото + осколки, owner-вход P-6; НЕ самоцветы,
 * НЕ слоты, НЕ XP). Числа — balance-CSV (колонки reward_gold / reward_shards):
 *   - частая (`common`):    **3g + 0sh** (мелкая инъекция новичку).
 *   - редкая (`rare`):      **4g + 3sh** (заметная за мастерство/упорство).
 *   - очень-редкая (`very_rare`): **8g + 6sh** (крупнее за преданность).
 * Сумма ачивко-faucet'а — мелкий one-time bonus (balance §P-6 PASS — не ломает темп
 * экономики гномов). Слотов НЕ даёт НИ один класс. balance CSV / spec §5.
 */
export const ACH_REWARD = {
    common: { gold: 3, shards: 0 }, // CSV rarity_class=частая
    rare: { gold: 4, shards: 3 }, // CSV rarity_class=редкая
    very_rare: { gold: 8, shards: 6 }, // CSV rarity_class=очень-редкая
};
// --- Пороги (balance-CSV `achievements-balance-v1.csv`; трасса = колонка threshold) -
/**
 * score_verified-тиры (`skill_score_milestone`). **CSV: 60 / 150 / 300**
 * (sim: P75 smart ≈50 → 60 humans>bot; P99 ≈106 → 150 human-scaled; 300 title-worthy,
 * telemetry-TBD). 3 ступени → частая / редкая / очень-редкая.
 */
export const ACH_SCORE_TIER = [60, 150, 300];
/** Длина партии (`skill_long_haul`). **CSV: 80** (sim: P99 moves smart=75, pmax 117 → 80). */
export const ACH_MOVES_TIER = 80;
/** Объём партий (`persist_games`). **CSV: 10 / 100 / 1000** (~дни / недели / месяцы). */
export const ACH_GAMES_TIER = [10, 100, 1000];
/** Daily-стрик (`persist_daily_streak`). **CSV: 7 / 30** (неделя / месяц; research §1.9). */
export const ACH_STREAK_TIER = [7, 30];
/** Зачётов досок дня (`persist_daily_count`). **CSV: 10 / 50** (~10 дней / 2 мес ритуала). */
export const ACH_DAILY_GAMES_TIER = [10, 50];
/** Уровень игрока (`persist_level`). **CSV: 10 / 30 / 60** (L10 ~дни / L30 ~недели / L60 soft-cap). */
export const ACH_LEVEL_TIER = [10, 30, 60];
/** Суммарно престижей гномов (`gnome_prestige`). **CSV: 1 / 10** (1-й престиж ~недели; 10 — месяцы). */
export const ACH_PRESTIGE_TIER = [1, 10];
/** Суммарно самоцветов в пуле (`coll_gems_total`). **CSV: 25 / 100** (десятки / долгий сбор). */
export const ACH_GEMS_TOTAL_TIER = [25, 100];
/** Суммарно намыто золота (`coll_gold_total`). **CSV: 1000 / 10000** (~неделя-месяц / ~2× гном-L20). */
export const ACH_GOLD_TIER = [1000, 10000];
/** Full-board-проигрышей (`fun_full_board_loss`). **CSV: 25** (sim: неизбежно; 25 = регулярная игра). */
export const ACH_FULLBOARD_TIER = 25;
/** Просмотрено реплеев (`fun_replay_watcher`). **CSV: 5 / 25** (telemetry-TBD; низкий порог вовлечения). */
export const ACH_WATCH_TIER = [5, 25];
/**
 * «Чистая линия» (`skill_efficient`): зачёт доски дня со счётом ≥ X, потратив ≤ Y
 * ходов. **CSV basis derived: score≥30 & moves≤30** (P25 ходов & median+ счёт).
 */
export const ACH_EFFICIENT_MOVES_MAX = 30;
export const ACH_EFFICIENT_SCORE_MIN = 30;
/** Топ-N all-time-борда (`mode_alltime_top`). **CSV: 100** (топ-100; telemetry-TBD по популяции). */
export const ACH_ALLTIME_TOP_N = 100;
/** Звезда (`skill_star`): `n ≥ 13` (тройное пересечение). **CSV: 13** (sim: bot-unreachable; telemetry-TBD). */
export const ACH_STAR_CLEAR_N = 13;
/** «Перекрестье» / «Первый самоцвет» — порог клира n≥10 (= `GEM_MIN_CLEAR`). **CSV: 10**. */
export const ACH_CROSS_CLEAR_N = GEM_MIN_CLEAR;
/** «Слепая девятка» (`mode_nine_blind`): `n ≥ 9` в blind (= `SHARDS_MIN_CLEAR`). **CSV: 9**. */
export const ACH_NINE_CLEAR_N = SHARDS_MIN_CLEAR;
/** «Чистый расчёт» (`mode_blind_score`): счёт ≥ порога в blind. **CSV: 120** (sim: blind P99 ~55-65, human-scaled). */
export const ACH_BLIND_SCORE_TIER = 120;
/** Минимум линий, снятых в одном move/тике, для «Каскадёр» (`skill_double_clear`). **= 2** (spec §3: ≥2 линии). */
export const ACH_DOUBLE_CLEAR_MIN_LINES = 2;
/**
 * «С самого дна» (`fun_comeback`): после ≥ X занятых клеток (из 81) счёт вырос ещё
 * на ≥ Y за партию. **CSV basis sim: fill≥60 & gain≥10** (P~0.075 партий → редкая).
 */
export const ACH_COMEBACK_FILLED_MIN = 60;
export const ACH_COMEBACK_SCORE_GAIN = 10;
/**
 * Канонический каталог 28 ачивок (`docs/specs/achievements.md` §3). `id` — стабильные
 * ключи (НЕ меняются, пишутся в `player_achievements`). Названия/описания — клиент
 * рисует из локали по `id` (`lib/achievement-catalog`); каталог несёт только механику.
 * Пороги/награды — balance-CSV (см. `ACH_*_TIER` + `ACH_REWARD` выше).
 *
 * **Состав (spec §3, «Итого 28»):** 8 скилл + 7 упорство/прогрессия + 4 коллекция +
 * 4 режимы + 5 discovery = 28. Скрытых = 3 (`fun_buzzer_beater`/`fun_full_board_loss`/
 * `fun_comeback`) ≈ 11% (≤15%, AC-7). С под-тирами = 38 разблокировок.
 *
 * **Идемпотентность (AC-2):** каждая (id, tier_index) выдаётся раз (PK
 * `(player_id, achievement_id, tier_index)` + ON CONFLICT DO NOTHING).
 */
export const ACHIEVEMENT_CATALOG = [
    // --- Скилл — мастерство ядра (8) ---
    { id: 'skill_first_cross', category: 'skill', trigger: 'first_clear_n', tiers: [ACH_CROSS_CLEAR_N], rewardRarityByStep: ['common'] },
    { id: 'skill_first_gem', category: 'skill', trigger: 'first_gem', tiers: [1], rewardRarityByStep: ['common'] },
    { id: 'skill_star', category: 'skill', trigger: 'first_clear_n', tiers: [ACH_STAR_CLEAR_N], rewardRarityByStep: ['very_rare'] },
    { id: 'skill_score_milestone', category: 'skill', trigger: 'score_in_game', tiers: ACH_SCORE_TIER, rewardRarityByStep: ['common', 'rare', 'very_rare'] },
    { id: 'skill_long_haul', category: 'skill', trigger: 'moves_in_game', tiers: [ACH_MOVES_TIER], rewardRarityByStep: ['rare'] },
    { id: 'skill_efficient', category: 'skill', trigger: 'efficient_daily', tiers: [1], rewardRarityByStep: ['rare'] },
    { id: 'skill_rainbow_run', category: 'skill', trigger: 'all_colors_in_game', tiers: [1], rewardRarityByStep: ['rare'] },
    { id: 'skill_double_clear', category: 'skill', trigger: 'double_clear', tiers: [ACH_DOUBLE_CLEAR_MIN_LINES], rewardRarityByStep: ['very_rare'] },
    // --- Упорство / прогрессия — natural-overlap гринд (7) ---
    { id: 'persist_games', category: 'persist', trigger: 'games_total', tiers: ACH_GAMES_TIER, rewardRarityByStep: ['common', 'rare', 'very_rare'] },
    { id: 'persist_daily_streak', category: 'persist', trigger: 'daily_streak', tiers: ACH_STREAK_TIER, rewardRarityByStep: ['rare', 'very_rare'] },
    { id: 'persist_daily_count', category: 'persist', trigger: 'daily_count', tiers: ACH_DAILY_GAMES_TIER, rewardRarityByStep: ['common', 'rare'] },
    { id: 'persist_level', category: 'persist', trigger: 'player_level', tiers: ACH_LEVEL_TIER, rewardRarityByStep: ['common', 'rare', 'very_rare'] },
    { id: 'gnome_first_unlock', category: 'persist', trigger: 'gnome_first_unlock', tiers: [1], rewardRarityByStep: ['common'] },
    { id: 'gnome_all_seven', category: 'persist', trigger: 'gnome_all_seven', tiers: [1], rewardRarityByStep: ['rare'] },
    { id: 'gnome_prestige', category: 'persist', trigger: 'gnome_prestige_total', tiers: ACH_PRESTIGE_TIER, rewardRarityByStep: ['rare', 'very_rare'] },
    // --- Коллекция — самоцветы / гномы, без RNG-лотереи (4) ---
    { id: 'coll_gem_each_color', category: 'collection', trigger: 'gem_each_color', tiers: [1], rewardRarityByStep: ['rare'] },
    { id: 'coll_gems_total', category: 'collection', trigger: 'gems_total', tiers: ACH_GEMS_TOTAL_TIER, rewardRarityByStep: ['common', 'rare'] },
    { id: 'coll_gold_total', category: 'collection', trigger: 'gold_total', tiers: ACH_GOLD_TIER, rewardRarityByStep: ['common', 'rare'] },
    { id: 'gnome_max_level', category: 'collection', trigger: 'gnome_max_level', tiers: [1], rewardRarityByStep: ['rare'] },
    // --- Мастерство режимов (4) ---
    { id: 'mode_cover_all', category: 'mode', trigger: 'modes_covered', tiers: [1], rewardRarityByStep: ['common'] },
    { id: 'mode_nine_blind', category: 'mode', trigger: 'clear_n_in_mode', tiers: [ACH_NINE_CLEAR_N], rewardRarityByStep: ['rare'], mode: 'blind' },
    { id: 'mode_blind_score', category: 'mode', trigger: 'score_in_mode', tiers: [ACH_BLIND_SCORE_TIER], rewardRarityByStep: ['very_rare'], mode: 'blind' },
    { id: 'mode_alltime_top', category: 'mode', trigger: 'alltime_top', tiers: [ACH_ALLTIME_TOP_N], rewardRarityByStep: ['rare'] },
    // --- Discovery / забавные — 3 скрытых-сюрприза (5) ---
    { id: 'fun_first_share', category: 'fun', trigger: 'first_share', tiers: [1], rewardRarityByStep: ['common'] },
    { id: 'fun_replay_watcher', category: 'fun', trigger: 'replay_watch_total', tiers: ACH_WATCH_TIER, rewardRarityByStep: ['common', 'rare'] },
    { id: 'fun_buzzer_beater', category: 'fun', trigger: 'rollover_daily', tiers: [1], rewardRarityByStep: ['rare'], hidden: true },
    { id: 'fun_full_board_loss', category: 'fun', trigger: 'fullboard_losses', tiers: [ACH_FULLBOARD_TIER], rewardRarityByStep: ['common'], hidden: true },
    { id: 'fun_comeback', category: 'fun', trigger: 'comeback', tiers: [1], rewardRarityByStep: ['rare'], hidden: true },
];
// =============================================================================
// Мета «Гномы» — 7 гномов-добытчиков (`meta-gnomes.md` §4.5 реестр констант).
// ЗАЧЕМ (CLAUDE.md no-hardcode + code-structure §4 + AC-Gn16): ВСЕ числа гномов
// (макс. уровень, пороги раскрытия бонусов, per-tick кривые добычи, цены апгрейда,
// перм-дельты престижа) — здесь, не литералами в UI/логике/faucet. Один источник →
// клиент и сервер не расходятся. UI-тикеты GN-1/2 используют structural-константы
// (макс. уровень + пороги unlock); faucet/мутации (GN-4/GN-5) читают кривые + цены.
//
// **ЧИСЛА BALANCED + owner-approved 2026-06-17** (economy-balancer, спека rev 0.2 §4.5).
// Зашиты ЯВНЫМИ таблицами L1..20 (методология balance «круглые числа» — ровные
// потолки L20). Источник чисел (НЕ выдуманы):
//   - `docs/balance/meta-gnomes/gnomes_production_curves.csv` — `GNOME_*_CURVE` (per-tick %).
//   - `docs/balance/meta-gnomes/gnomes_upgrade_costs.csv` — `GNOME_UPGRADE_COST_*` (L→L+1).
//   - `docs/balance/meta-gnomes/gnomes_key_numbers.csv` — пороги/дельты/потолки/cum.
//   - `docs/balance/meta-gnomes/gnomes-balance-report.{md,html}` — обоснование + темп.
// Owner-lock structural (макс=20, пороги unlock 1/5/15, shard-cost-from L15,
// перм-дельты 3%/1%/0.5%) — meta-gnomes.md §2.2/§2.4/§4.5 (owner-directive 2026-06-13
// + balance 2026-06-17). Сверка cum c CSV: золото 1 гном→L20 = 4595, осколки = 275
// (= суммы таблиц `GNOME_UPGRADE_COST_GOLD`/`_SHARDS` ниже; проверяется тестом).
// =============================================================================
/**
 * Макс. уровень гнома. **= 20 (owner-lock 2026-06-13).** На L20 апгрейд недоступен —
 * вместо «Прокачать» карточка показывает «Престиж» (reset L1 + перм-бонус). UI-карточка
 * рисует прогресс `level / GNOME_MAX_LEVEL`. meta-gnomes.md §2.3/§4.5 `GNOME_MAX_LEVEL`.
 */
export const GNOME_MAX_LEVEL = 20;
/**
 * Уровень, с которого активен бонус ЗОЛОТА гнома. **= 1 (owner-lock 2026-06-13)** —
 * золото с рождения. UI: карточка показывает золото-бонус всегда открытым.
 * meta-gnomes.md §2.2/§4.5 `GNOME_GOLD_UNLOCK` / I-Gn5.
 */
export const GNOME_GOLD_UNLOCK = 1;
/**
 * Уровень, с которого открывается бонус ОСКОЛКОВ гнома. **= 5 (owner-lock 2026-06-13).**
 * До L5 вклад осколков спит. UI: карточка показывает осколки как «откроется на L5»
 * (приглушено) пока `level < 5`. meta-gnomes.md §2.2/§4.5 `GNOME_SHARDS_UNLOCK` / I-Gn5.
 */
export const GNOME_SHARDS_UNLOCK = 5;
/**
 * Уровень, с которого открывается бонус КАМНЯ своего цвета. **= 15 (owner-lock
 * 2026-06-13).** До L15 per-color шанс камня спит. UI: карточка показывает камень как
 * «откроется на L15». meta-gnomes.md §2.2/§4.5 `GNOME_GEM_UNLOCK` / I-Gn5.
 */
export const GNOME_GEM_UNLOCK = 15;
/**
 * Перм-дельта ЗОЛОТА за один престиж (доля; **+0.03 = +3%**, owner-approved
 * 2026-06-17, balance). Накапливается с каждым престижем (§2.4): накопленный
 * перм-бонус золота гнома = `prestige_count × GNOME_PRESTIGE_GOLD_PER`. UI: текст
 * confirm-диалога «навсегда +X% золота». meta-gnomes.md §2.4/§4.5 `GNOME_PRESTIGE_GOLD_PER`.
 */
export const GNOME_PRESTIGE_GOLD_PER = 0.03;
/** Перм-дельта ОСКОЛКОВ за один престиж (**+0.01 = +1%**, owner-approved 2026-06-17). §2.4/§4.5. */
export const GNOME_PRESTIGE_SHARDS_PER = 0.01;
/** Перм-дельта ШАНСА КАМНЯ за один престиж (**+0.005 = +0.5%**, owner-approved 2026-06-17). §2.4/§4.5. */
export const GNOME_PRESTIGE_GEM_PER = 0.005;
/**
 * Уровень, С КОТОРОГО апгрейд гнома требует ОСКОЛКИ (вдобавок к золоту). **= 15
 * (owner-approved 2026-06-17, balance §6 — сдвинут с 10).** До L15 апгрейд = чистое
 * золото (`GNOME_UPGRADE_COST_SHARDS` = 0 для L1..14); с L15 — золото + осколки.
 * Причина сдвига: ранняя осколочная стена убивала казуала (не кормит скилл-кран
 * осколков `n≥9` до L15) на годы. meta-gnomes.md §2.3/§4.5 `GNOME_SHARD_COST_FROM_LEVEL`.
 */
export const GNOME_SHARD_COST_FROM_LEVEL = 15;
/**
 * Per-tick шанс БОНУСНОЙ МОНЕТЫ (золото) одного гнома по уровню (L1..L20). Доля [0,1]
 * (CSV `золото_шанс_%` / 100). Каденция тика = `GOLD_PER_POINT_THRESHOLD[mode]` очков
 * (§2.5 rev 0.2). Активен с `GNOME_GOLD_UNLOCK` (=L1). Монотонно растёт; потолок 1
 * гнома L20 = **8%** (7 гномов → +56% золота макс — антиинфляция, §4.5). Индекс =
 * `level` (1-based); `[0]` sentinel = 0 (нет уровня 0). gnomes_production_curves.csv.
 */
export const GNOME_GOLD_CURVE = [
    // [0] — sentinel (нет уровня 0).
    0,
    0.005, 0.008, 0.011, 0.014, 0.02, // L1..L5
    0.025, 0.03, 0.035, 0.04, 0.05, // L6..L10
    0.055, 0.06, 0.065, 0.07, 0.075, // L11..L15
    0.077, 0.079, 0.08, 0.08, 0.08, // L16..L20 (потолок 8%)
];
/**
 * Per-tick шанс БОНУСНОГО ОСКОЛКА одного гнома по уровню (L1..L20). Доля [0,1]
 * (CSV `осколки_шанс_%` / 100). Очко-зависимый кран, ОТДЕЛЬНЫЙ от скилл-осколков
 * (`n≥9`, §2.5). Спит до `GNOME_SHARDS_UNLOCK` (=L5 → `[1..4]` = 0). Потолок 1 гнома
 * L20 = **3%**. Индекс = `level` (1-based); `[0]` sentinel. gnomes_production_curves.csv.
 */
export const GNOME_SHARDS_CURVE = [
    // [0] — sentinel.
    0,
    0, 0, 0, 0, 0.005, // L1..L5 (спит до L5; L5 = 0.5%)
    0.007, 0.009, 0.011, 0.013, 0.015, // L6..L10
    0.018, 0.02, 0.022, 0.024, 0.026, // L11..L15
    0.027, 0.028, 0.029, 0.03, 0.03, // L16..L20 (потолок 3%)
];
/**
 * Per-tick шанс БОНУСНОГО КАМНЯ СВОЕГО ЦВЕТА одного гнома по уровню (L1..L20). Доля
 * [0,1] (CSV `камень_шанс_%` / 100). Per-color (гном X → только цвет X), очко-зависимый,
 * ОТДЕЛЬНЫЙ от скилл-камня (`n≥10`, 100%, §2.5). Спит до `GNOME_GEM_UNLOCK` (=L15 →
 * `[1..14]` = 0). Потолок 1 гнома L20 = **0.4%** (камень редкий). Индекс = `level`
 * (1-based); `[0]` sentinel. gnomes_production_curves.csv.
 */
export const GNOME_GEM_CURVE = [
    // [0] — sentinel.
    0,
    0, 0, 0, 0, 0, // L1..L5
    0, 0, 0, 0, 0, // L6..L10
    0, 0, 0, 0, 0.001, // L11..L15 (спит до L15; L15 = 0.1%)
    0.0015, 0.002, 0.0028, 0.0035, 0.004, // L16..L20 (потолок 0.4%)
];
/**
 * Стоимость апгрейда гнома L→L+1 в ЗОЛОТЕ, индексированная текущим уровнем `L`
 * (1-based; `GNOME_UPGRADE_COST_GOLD[L]` = цена перехода L→L+1). Экспо, фронт-дешёвая.
 * Длина 20: `[0]` sentinel, `[1..19]` — цены L1→2 … L19→20, `[20]` = 0 (с L20 апгрейда
 * нет — только престиж). Σ([1..19]) = **4595** (cum 1 гном→L20, сверка с CSV / тест).
 * gnomes_upgrade_costs.csv `цена_золото`.
 */
export const GNOME_UPGRADE_COST_GOLD = [
    // [0] — sentinel (нет уровня 0).
    0,
    8, 12, 18, 28, 40, // L1→2 .. L5→6
    55, 72, 92, 115, 145, // L6→7 .. L10→11
    180, 220, 270, 330, 400, // L11→12 .. L15→16
    480, 580, 700, 850, // L16→17 .. L19→20
    0, // [20] — на L20 апгрейда нет (престиж).
];
/**
 * Стоимость апгрейда гнома L→L+1 в ОСКОЛКАХ, индексированная текущим уровнем `L`
 * (1-based; `GNOME_UPGRADE_COST_SHARDS[L]` = осколки перехода L→L+1). **0 до L15**
 * (`GNOME_SHARD_COST_FROM_LEVEL` — до L15 чистое золото), с L15→16 — растущая цена.
 * Длина 20: `[0]` sentinel, `[1..19]` цены, `[20]` = 0. Σ([1..19]) = **275** (cum
 * осколков 1 гном→L20, сверка с CSV / тест). gnomes_upgrade_costs.csv `цена_осколки`.
 */
export const GNOME_UPGRADE_COST_SHARDS = [
    // [0] — sentinel.
    0,
    0, 0, 0, 0, 0, // L1→2 .. L5→6
    0, 0, 0, 0, 0, // L6→7 .. L10→11
    0, 0, 0, 0, 25, // L11→12 .. L15→16 (осколки включаются на L15→16)
    35, 50, 70, 95, // L16→17 .. L19→20
    0, // [20] — престиж.
];
/**
 * Стоимость апгрейда гнома с уровня `fromLevel` на `fromLevel + 1` (чистая функция
 * config, AC-Gn16). Вне диапазона 1..MAX-1 → стоимость 0 (на L20 апгрейда нет;
 * вызывающий проверяет `level < GNOME_MAX_LEVEL` отдельно). Осколки = 0 до
 * `GNOME_SHARD_COST_FROM_LEVEL`.
 *
 * @param fromLevel — текущий уровень гнома (апгрейд ведёт на `fromLevel + 1`).
 * @returns стоимость в золоте + осколках (server-truth; клиент рисует ту же).
 */
export function gnomeUpgradeCost(fromLevel) {
    return {
        gold: GNOME_UPGRADE_COST_GOLD[fromLevel] ?? 0,
        shards: GNOME_UPGRADE_COST_SHARDS[fromLevel] ?? 0,
    };
}
// =============================================================================
// Вклад одного гнома в категорию добычи — ЕДИНЫЙ ИСТОЧНИК формулы (meta-gnomes.md
// §2.2/§2.5; тикет fe-gnome-bonus-display §«Где разместить»). ЗАЧЕМ ЗДЕСЬ (а не
// дублём в server `gnomes-state.ts` + client `gnome-state.ts`): сервер минтит
// растущую добычу по `levelChance + prestigeCount × prestigePer`; клиентский ПОКАЗ
// бонуса обязан считаться по ТОЙ ЖЕ формуле, иначе UI «врёт» про server-truth (дрейф
// клиент↔сервер). Вынос в shared (направление `shared → client|server`, §7 code-
// structure) делает обе стороны вызывающими ОДНУ функцию — дрейф невозможен (AC-2).
// Числа — config-кривые/пороги/перм-дельты выше (no-hardcode). Чистые функции.
// =============================================================================
/**
 * Уровневый вклад гнома в категорию по его текущему уровню (читает config-кривую).
 * До порога открытия категории кривая = 0 (I-Gn5 встроен в таблицы), но порог
 * проверяем явно ради ясности/защиты. Единый источник — зовут И сервер (faucet/
 * проекция), И клиент (показ бонуса карточки/детали).
 *
 * @param level — уровень гнома (1-based; вне диапазона → 0 защитно).
 * @param curve — config-кривая категории (`GNOME_*_CURVE`).
 * @param unlock — порог открытия категории (`GNOME_*_UNLOCK`).
 * @returns доля [0,1+] вклада уровня (НЕ ×100; форматирование — на стороне UI).
 */
export function gnomeLevelChance(level, curve, unlock) {
    if (level < unlock)
        return 0;
    return curve[level] ?? 0;
}
/**
 * Полный вклад одного гнома в категорию: уровневый бонус + накопленный перм-престиж
 * (`prestige_count × дельта`, §2.5). Перм-бонус активен только если категория открыта
 * (гном с L3 не имеет осколочного перм-бонуса — осколки спят до L5; I-Gn5). **ЕДИНЫЙ
 * ИСТОЧНИК (AC-2):** сервер (`gnomes-state.ts:computeGnomeProduction`) и клиент
 * (`gnome-state.ts:gnomeBonusPercent`) зовут ЭТУ функцию — клиент=сервер байт-в-байт.
 *
 * @param level — уровень гнома.
 * @param prestigeCount — число престижей (≥0).
 * @param curve — config-кривая категории.
 * @param unlock — порог открытия категории.
 * @param prestigePer — перм-дельта за престиж в этой категории (`GNOME_PRESTIGE_*_PER`).
 * @returns доля [0,1+] суммарного вклада (НЕ ×100; форматирование — на стороне UI).
 */
export function gnomeContribution(level, prestigeCount, curve, unlock, prestigePer) {
    if (level < unlock)
        return 0;
    return gnomeLevelChance(level, curve, unlock) + prestigeCount * prestigePer;
}
// =============================================================================
// Unlock-schedule гномов (GN-7; meta-gnomes.md §2.0 / §4.5 «Unlock-schedule», rev 0.3).
// ОТДЕЛЬНАЯ ось от прокачки/крана выше (две независимые оси, §4.5): здесь — на каких
// УРОВНЯХ ИГРОКА (`player_level`) открывается следующий (случайный-из-оставшихся) гном.
// owner-approved 2026-06-19; калибровка (отчёт gnome-unlock-balance-report): активный
// 8 партий/день → все 7 за ~2 недели (7-я веха = ур.31), казуал 1/день → ~1.5 мес;
// 1-я веха рано (кран денег меты течёт у всех в день 1). Числа — НЕ выдуманы.
// =============================================================================
/**
 * Пороги УРОВНЯ ИГРОКА (`player_level`), на каждом из которых открывается один
 * (случайный-из-ещё-не-открытых) гном (§2.0, AC-Gn16). **owner-approved 2026-06-19:
 * `[2, 6, 11, 17, 23, 27, 31]`** — 7 монотонно возрастающих вех.
 *
 * Индекс 0-based: `GNOME_UNLOCK_LEVELS[k]` = порог `(k+1)`-й вехи. `gnome_unlocks_granted`
 * (счётчик уже выданных вех, server-truth) индексирует СЛЕДУЮЩУЮ веху:
 * пока `player_level >= GNOME_UNLOCK_LEVELS[gnome_unlocks_granted]` — открыть гнома,
 * инкрементить счётчик (возможна выдача нескольких за партию при перепрыге вех).
 *
 * **Balance-требование (owner-lock §2.0):** `[0]` (1-я веха) — РАНО (=2, минимум: L1
 * запрещён, иначе «открыт при старте»), чтобы гном-кран начал течь как можно скорее;
 * `[6]` (7-я веха) — амбиция (=31, уровень активного на день 14). Числа прокачки/крана
 * (`GNOME_*_CURVE` / `GNOME_UPGRADE_COST_*` / перм-дельты) этим НЕ затронуты.
 *
 * Длина = `GNOME_COLORS` (=7): ровно 7 вех на 7 гномов (после 7-й все открыты,
 * `next_unlock_level` = null). meta-gnomes.md §2.0/§4.5 `GNOME_UNLOCK_LEVELS` / AC-Gn16.
 */
export const GNOME_UNLOCK_LEVELS = [2, 6, 11, 17, 23, 27, 31];
/**
 * Namespace-префикс HMAC-stream выбора цвета гнома на вехе (§2.0/§7, паттерн ADR-0015
 * D-4). Версионирован: смена развёртки → `v2` для будущих открытий (прошлые open-
 * события хранят ИТОГ в `meta_ledger`, не пересчёт). Keyed на `player_id + milestone_index`
 * (НЕ `game_id`): открытие привязано к игроку+вехе, чтобы rebuild был стабилен (AC-Gn17).
 *
 *   choose(milestone) = HMAC-SHA256(LINES98_FAUCET_SECRET,
 *                         "lines98-gnome-unlock-v1:" + player_id + ":" + milestone_index)
 *   → rejection-sample digest-байтов → индекс в remaining-множестве (`pickFromRemaining`).
 */
export const GNOME_UNLOCK_NAMESPACE = 'lines98-gnome-unlock-v1';
/**
 * Выбрать цвет гнома из множества ещё-не-открытых по байтам HMAC-дайджеста —
 * **rejection-sampling без modulo-bias** (§2.0/§7, AC-Gn17). Чистая функция: вся
 * случайность инкапсулирована во входных `digestBytes` (HMAC считается server-side из
 * секрета); тут — только детерминированная развёртка дайджеста в индекс.
 *
 * **Алгоритм (без modulo-bias):** для `m = remaining.length` берём наибольший
 * `limit = floor(256 / m) * m` (максимум кратный `m`, ≤ 256). Идём по байтам потока:
 * если `byte < limit` → принимаем `idx = byte % m` и возвращаем `remaining[idx]`;
 * иначе (byte в «хвосте» `[limit, 256)`, который перекосил бы распределение) —
 * ОТБРАСЫВАЕМ байт и берём следующий. Так каждый из `m` исходов равновероятен.
 *
 * Детерминизм/инвариант (AC-Gn17): тот же `digestBytes` + то же `remaining` → тот же
 * цвет. Если поток байтов исчерпан без принятия (астрономически маловероятно для
 * `m ≤ 7`: P(reject) ≤ 16/256 на байт) — фолбэк на последний байт `% m` (страховка от
 * зацикливания; на практике недостижим при 32-байтном SHA-256 дайджесте и m ≤ 7).
 *
 * @param digestBytes — поток байтов HMAC-дайджеста (Uint8Array / number[], значения 0..255).
 * @param remaining — ещё-не-открытые цвета (непустой; напр. `[1,3,4,5,6,7]`).
 * @returns выбранный цвет из `remaining` (равновероятно, детерминированно).
 */
export function pickFromRemaining(digestBytes, remaining) {
    const m = remaining.length;
    if (m <= 0)
        throw new Error('pickFromRemaining: remaining must be non-empty');
    if (m === 1)
        return remaining[0]; // единственный кандидат — без выбора
    // Наибольшее кратное m, не превосходящее 256 — граница приёма (анти-bias).
    const limit = Math.floor(256 / m) * m;
    for (let i = 0; i < digestBytes.length; i++) {
        const byte = digestBytes[i] & 0xff;
        if (byte < limit) {
            return remaining[byte % m];
        }
        // byte в хвосте [limit, 256) → отбрасываем (rejection), берём следующий.
    }
    // Страховка от исчерпания потока (недостижимо при SHA-256 32B + m ≤ 7): последний
    // байт % m. Детерминированно, чтобы функция всегда возвращала валидный цвет.
    const last = (digestBytes[digestBytes.length - 1] & 0xff) % m;
    return remaining[last];
}
// =============================================================================
// Смена ника (Профиль; profile-dashboard.md §2.6 / §4 / §7-2; ADR-0019).
//
// Игрок меняет публичный `players.display_name` за золото. Цель owner'а (R-6,
// owner-fixed 2026-06-21): НЕ дать менять ники часто — снять нагрузку на БД и
// анти-абьюз. **Это косметика/анти-абьюз, НЕ p2w (I-1):** ник — display-only,
// не влияет на счёт/ранг/попытки/честные лидерборды (I-Prof-1). ПЕРВАЯ установка
// ника (с дефолтного/пустого) — БЕСПЛАТНО; последующие СМЕНЫ — платно.
//
// Число owner-fixed (economy-balancer НЕ задействован — это не баланс экономики, а
// анти-частые-смены, R-6). В config (CLAUDE.md hard-rule), не литералом в логике.
// =============================================================================
/**
 * Стоимость СМЕНЫ ника в золоте (`players.display_name`). **owner-fixed 2026-06-21
 * = 1000** (profile-dashboard.md §2.6 / R-6). Списывается из `meta_balances.gold`
 * атомарно вместе со сменой ника (server-truth, клиент не списывает локально,
 * I-Prof-3). Применяется ТОЛЬКО к смене уже-заданного ника; ПЕРВАЯ установка
 * (с NULL/дефолта) — бесплатна (profile-dashboard.md §10 Q-1 / §2.5). Display-only
 * трата — НЕ p2w (I-1 / I-Prof-1).
 *
 * Число — owner-decision (НЕ economy-balancer): цель = снять нагрузку на БД от
 * частых смен, не экономический баланс (R-6). Ребаланс — правка тут (один источник).
 */
export const NICKNAME_CHANGE_COST = 1000;
//# sourceMappingURL=meta.js.map